import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { SuggestUpgradeTool } from 'js/src/agent/tools/sketch/SuggestUpgradeTool';
import type { RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    deleteLuminaryOverlayOperation,
    deleteSceneGroupOperation,
} from 'js/src/luminary/luminary-operations';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

export const legacyDeleteSceneSchema = z.object({
    scene_index: z.number().describe('The scene index to delete'),
});
export type DeleteSceneArgs = z.infer<typeof legacyDeleteSceneSchema>;

export class LegacyDeleteSceneTool extends VideoStateTool<typeof legacyDeleteSceneSchema> {
    public static readonly toolName = 'delete_scene';

    protected override readonly schema = legacyDeleteSceneSchema;
    protected override readonly toolName = LegacyDeleteSceneTool.toolName;
    protected override readonly toolDescription = 'Delete a scene from the video';

    public override getToolDisplayName(): string {
        return 'Deleting scene';
    }

    public get statusIcon(): string {
        return 'delete';
    }

    public async execute(args: DeleteSceneArgs, context: ToolExecutionContext) {
        const { scene_index } = args;
        const { parentActionGroup } = context;

        try {
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds. Video has ${this.luminary.scenes.length} scenes`;
            }

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene at index ${scene_index}`;
            }

            // The Lumen5 branding scene is required on the free plan and can only be
            // removed by users whose plan allows removing branding.
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                const canRemoveBranding = SERVER?.USER?.capabilities?.no_branding ?? false;
                if (!canRemoveBranding) {
                    return `Error: This is the Lumen5 branding scene, which is required on the free plan and cannot be deleted. Use the ${SuggestUpgradeTool.toolName} tool (feature "remove_branding") to let the user upgrade to remove Lumen5 branding.`;
                }
            }

            // Check if this is a voice-driven video (talking head or voiceover)
            if (isVoiceDrivenSceneGroup(sceneGroup)) {
                if (!isSceneAnOverlay(this.getState(), scene)) {
                    return `Error: Scene ${scene_index} is the base/presenter scene of a voice-driven scene group, which carries the voice-over and controls the group's timing, so it can't be deleted. You can delete a scene added to this scene group, or edit the transcript or replace the voice-over to change the base content.`;
                }

                try {
                    const { deleteLuminaryOverlay } = makeOperationsCallable(
                        { deleteLuminaryOverlayOperation },
                        this.luminary,
                        this.dispatch,
                        this.getState as () => RootState,
                    );

                    // The operation defrags surrounding scenes and checkpoints itself.
                    deleteLuminaryOverlay(scene.id, sceneGroup.id, false, parentActionGroup);

                    return `Success: Deleted scene at index ${scene_index}. The video now has ${this.luminary.scenes.length} scenes.`;
                } catch (error) {
                    const errorMessage =
                        error instanceof Error ? error.message : 'Unknown error occurred';
                    return `Error deleting scene: ${errorMessage}`;
                }
            }

            // currently only support deleting scene groups containing a single scene (shouldn't happen due to above
            // voicedriven check)
            if (sceneGroup.scenes.length !== 1) {
                return `Error: Cannot delete scene at index ${scene_index} because it contains multiple scenes.`;
            }

            const sceneGroupId = sceneGroup.id;

            const { deleteSceneGroup } = makeOperationsCallable(
                {
                    deleteSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState, // NOTE: no state currently
            );

            try {
                // Use the existing deleteSceneGroupOperation which handles validation
                // Pass parentActionGroup to ensure proper undo/redo grouping
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);

                return `Success: Deleted scene at index ${scene_index}. The video now has ${this.luminary.scenes.length} scenes.`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error deleting scene: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error deleting scene: ${errorMessage}`;
        }
    }
}
